import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Slider, InputAdornment, TextField } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { SelectInput } from '../../../Helpers/Inputs';
import { EditableBasicInfoSummary } from "../../Common/SubComponents/BasicInfoSummary";
import NumberFormatDollars from '../../../Helpers/Formatters/NumberFormatDollars';
import HealthClass from './HealthClass';
import Age from '../../../Helpers/Dates/Age';
import CoverageLengths from '../../../Helpers/Lists/CoverageLengths/TermCoverageLengths';
import TermInsuranceHealthTools from './TermInsuranceHealthTool';
import TermInsuranceNeedAnalysisTool from './TermInsuranceNeedAnalysisTool';
import TermInsuranceTermLengthTool from './TermInsuranceTermLengthTool';
import DropDownIcon from '../../../../icons/arrow_drop_down.svg?react';
import QuoterTheme from '../../Common/Helpers/BasicInfoQuoterTheme';

const SliderSection = React.memo(({ isAmerico, coverageAmount, setCoverageAmount, marks }) => {
  const [localCoverageAmount, setLocalCoverageAmount] = useState(coverageAmount);

  useEffect(() => {
    setLocalCoverageAmount(coverageAmount);
  }, [coverageAmount]);

  const handleSliderChange = useCallback((event, newValue) => {
    setLocalCoverageAmount(newValue); // Pure local, uncoupled from form context
  }, []);

  const handleSliderChangeCommitted = useCallback((event, newValue) => {
    setCoverageAmount(newValue); // Sync to parent/form only on release
  }, [setCoverageAmount]);

  const handleInputChange = useCallback((e) => {
    const rawValue = e.target.value.toString().replace(/[^0-9.-]+/g, "");
    const val = parseInt(rawValue, 10);
    if (!isNaN(val)) {
      setLocalCoverageAmount(val); // Local sync during typing
    } else if (rawValue === '') {
      setLocalCoverageAmount('');
    }
  }, []);

  const handleInputBlur = useCallback(() => {
    if (localCoverageAmount) {
      setCoverageAmount(localCoverageAmount); // Sync when leaving input
    } else {
      setLocalCoverageAmount(coverageAmount); // Revert if empty
    }
  }, [localCoverageAmount, coverageAmount, setCoverageAmount]);

  const inputPropsObj = useMemo(() => ({
    inputComponent: NumberFormatDollars,
    endAdornment: (
      <div className='select-end-adornment' onClick={() => document.getElementById("coverageAmount")?.focus()}>
        <InputAdornment position="end" className="text-input-carrot">
          <DropDownIcon />
        </InputAdornment>
      </div>
    ),
  }), []);

  return (
    <div className='select-slide-container'>
      <TextField
        name="coverageAmount"
        id="coverageAmount"
        label="Coverage Amount"
        value={localCoverageAmount}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        variant="filled"
        fullWidth
        slotProps={{ input: inputPropsObj }}
      />
      <div className="slider-container">
        <div className="mr-2 form-group">
          <Slider
            min={isAmerico ? 25000 : 100000}
            max={isAmerico ? 1000000 : 3000000}
            step={null}
            value={localCoverageAmount || 0}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            className="m-2"
            marks={marks}
          />
        </div>
      </div>
    </div>
  );
});

export default function GetQuote(props) {
  const healthClasses = useMemo(() => HealthClass(), []);
  const coverageLengths = useMemo(() => CoverageLengths(props.isAmerico), [props.isAmerico]);
  const parentTheme = useTheme();

  const theme = useMemo(() => createTheme(parentTheme, QuoterTheme()), [parentTheme]);

  const handleSetCoverageAmount = useCallback((val) => {
    props.setCoverageAmount(val);
  }, [props.setCoverageAmount]);

  const handleTermLengthChange = useCallback((e) => {
    props.setTermLength(e.target.value);
  }, [props.setTermLength]);

  const handleHealthClassChange = useCallback((e) => {
    props.setHealthClass(e.target.value);
  }, [props.setHealthClass]);

  const handleEditLinkClick = useCallback(() => {
    props.setOpenEditPopUp(true);
  }, [props.setOpenEditPopUp]);


  return (
    <div className="coverage-selector-container">
      <h2>{props.isAmerico ? "Loan Protection Life Insurance" : "Term Life Insurance"}</h2>
      <EditableBasicInfoSummary
        gender={props.basicInfo.gender}
        state={props.basicInfo.state}
        birthdate={props.basicInfo.birthdate || props.basicInfo.fakeBirthdate}
        tobacco={props.basicInfo.tobacco}
        height={props.basicInfo.height}
        weight={props.basicInfo.weight}
        showEditLink={true}
        editLinkOnClick={handleEditLinkClick}
      />
      <ThemeProvider theme={theme}>
        <Form>
          <div className='form-group'>
            <SliderSection
              isAmerico={props.isAmerico}
              coverageAmount={props.coverageAmount}
              setCoverageAmount={handleSetCoverageAmount}
              marks={props.marks}
            />
            <TermInsuranceNeedAnalysisTool
              toolName="Coverage Amount Calculator"
              title="Calculate how much coverage I need."
              setCoverageAmount={handleSetCoverageAmount}
            />
          </div>
          <div className='form-group'>
            <SelectInput
              name="CoverageLength"
              id="CoverageLength"
              value={props.termLength}
              hideEmptyOption
              onChange={handleTermLengthChange}
              label="Years of Coverage"
              selectOptions={coverageLengths}
            />
            <TermInsuranceTermLengthTool
              toolName="Term Length Calculator"
              title="Calculate the term length I need."
              insuredAge={Age(props.basicInfo.birthdate)}
              onTermLengthCalculation={(suggestedTermlength) => {
                props.setTermLength(suggestedTermlength);
              }}
            />
          </div>
          <div className='form-group'>
            <SelectInput
              name="HealthClass"
              id="HealthClass"
              hideEmptyOption
              value={props.healthClass}
              onChange={handleHealthClassChange}
              label="Health Rating"
              selectOptions={healthClasses}
            />
            <TermInsuranceHealthTools
              toolName="Health Rating Calculator"
              title="Calculate the health rating that fits me."
              healthClass={props.healthClass}
              loading={props.loading}
              errorMessage={props.errorMessage}
              setErrorMessage={props.setErrorMessage}
              isQuoteSaved={props.isQuoteSaved}
              setQuoteSaved={props.setQuoteSaved}
              quoteLogId={props.quoteLogId}
              terms={props.termLength}
              onHealthClassChange={props.onHealthClassChange}
            />
          </div>
        </Form>
      </ThemeProvider>
    </div>
  )
}
